/**
 * Web Push and inbox helpers with no Cloudflare bindings, so the Worker, the
 * SPA and the scripts check can share them.
 */

export type WebPushSubscription = {
  endpoint: string;
  keys: { p256dh: string; auth: string };
};

export type PushPayload = {
  title: string;
  body: string;
  url: string;
  event?: string | null;
  actorUserId?: string | null;
  /** ISO. Omitted means the 30-day TTL. `null` means no timer. */
  expiresAt?: string | null;
};

export const NOTICE_FILTERS = ["all", "trash"] as const;
export type NoticeFilter = (typeof NOTICE_FILTERS)[number];

export const NOTICE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const TITLE_MAX = 120;
const BODY_MAX = 500;
const USER_AGENT_MAX = 512;

export function noticeTtlExpiresAt(now = new Date()) {
  return new Date(now.getTime() + NOTICE_TTL_MS).toISOString();
}

/** Push services return these when the subscription is gone. Drop the row. */
export function isStalePushStatus(status: number) {
  return status === 404 || status === 410;
}

/**
 * Same-origin click target, as a path. `javascript:`, other hosts, and
 * protocol-relative URLs fall back to `/`.
 */
export function pushClickPath(url: string | null | undefined, origin: string): string {
  if (!url) return "/";
  const trimmed = url.trim();
  if (!trimmed) return "/";
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    if (trimmed.includes("\\") || trimmed.includes("://")) return "/";
    return trimmed;
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.origin !== origin) return "/";
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || "/";
  } catch {
    return "/";
  }
}

export function parseWebSubscription(input: unknown): WebPushSubscription | null {
  if (!input || typeof input !== "object") return null;
  const row = input as Record<string, unknown>;
  const endpoint = typeof row.endpoint === "string" ? row.endpoint.trim() : "";
  if (!endpoint.startsWith("https://")) return null;
  try {
    const parsed = new URL(endpoint);
    if (parsed.protocol !== "https:") return null;
  } catch {
    return null;
  }
  const keys = row.keys;
  if (!keys || typeof keys !== "object") return null;
  const keyRow = keys as Record<string, unknown>;
  const p256dh = typeof keyRow.p256dh === "string" ? keyRow.p256dh.trim() : "";
  const auth = typeof keyRow.auth === "string" ? keyRow.auth.trim() : "";
  if (!p256dh || !auth) return null;
  return { endpoint, keys: { p256dh, auth } };
}

export function clipNoticeTitle(value: string) {
  return value.trim().slice(0, TITLE_MAX);
}

export function clipNoticeBody(value: string) {
  return value.trim().slice(0, BODY_MAX);
}

export function clipUserAgent(value: string | null | undefined) {
  return (value ?? "").trim().slice(0, USER_AGENT_MAX) || null;
}

export function pushEndpointHost(endpoint: string) {
  try {
    return new URL(endpoint).host;
  } catch {
    return "";
  }
}

/** ISO instant, or null if missing or junk. */
export function parseNoticeSince(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ms = Date.parse(trimmed);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString();
}

/** Avatar badge label. Empty string means hide the badge. */
export function formatUnreadBadge(count: number) {
  if (!Number.isFinite(count) || count <= 0) return "";
  if (count > 99) return "99+";
  return String(Math.floor(count));
}

export type NoticeSkin = "ios" | "android";

/** iPhone/iPad/iPod and Macintosh get the iOS chrome; everything else Android. */
export function noticeSkin(ua = ""): NoticeSkin {
  if (/iphone|ipad|ipod/i.test(ua) || /macintosh|mac os x/i.test(ua)) return "ios";
  return "android";
}

export function parseNoticeFilter(value: unknown): NoticeFilter {
  return typeof value === "string" && (NOTICE_FILTERS as readonly string[]).includes(value)
    ? (value as NoticeFilter)
    : "all";
}
